import * as R from 'ramda'

/**
 * Create a promise queue manager instance
 * @param {Object} stateFlagsInstance - State flag instance
 * @returns {Object} Promise queue manager scope containing methods to resolve/isQueued and more
 */
export function createPromiseQueueManager(stateFlagsInstance) {
  stateFlagsInstance = stateFlagsInstance || createStateFlagsManager()
  let promises = [] //Temporal queue of promises

  setInterval(() => {
    promises.length = 0 //Clear queue if issues
  }, 30000)

  const scope = {
    getPromises() {
      return promises
    },
    isQueued(queueName = '') {
      return (
        promises.length > 0 && stateFlagsInstance.has(`${queueName}_resolving`)
      )
    },
    isResolved(queueName = '') {
      return stateFlagsInstance.has(`${queueName}_resolved`)
    },
    async isQueuedOrResolved(queueName, flush = true) {
      let isQueued = scope.isQueued(queueName)
      if (isQueued || scope.isResolved(queueName)) {
        if (isQueued && flush) {
          scope.flushPromises(queueName)
        }
        return true
      } else {
        return false
      }
    },
    async flushPromises() {
      await Promise.all(promises)
    },
    async queueResolve(queueName, promiseToQueue) {
      if (!queueName) {
        throw new Error('queueName expected')
      }
      if (!promiseToQueue || !(promiseToQueue instanceof Promise)) {
        throw new Error('promise (Promise) required')
      }
      stateFlagsInstance.add(`${queueName}_resolving`)
      promises.push(promiseToQueue)
      const res = await promiseToQueue
      promises = promises.slice(0, -1)
      stateFlagsInstance.clear(`${queueName}_resolving`)
      stateFlagsInstance.add(`${queueName}_resolved`)
      return res
    },
  }
  return scope
}

/**
 * Manages state flags
 * Use case: A loading flag to compute a temporal message "Data loading..."
 * Extracted from zones-module.js
 *
 * @param {Object} _store Optional Vue vuex store object to sync a timestamp (For reactivity)
 * @param {String} statePropertyName optional vuex store state property name
 *
 * @returns {Object} Methods for adding, clearing and resetting state flags and checking if a flag is set.
 */
export function createStateFlagsManager() {
  let stateFlags = []
  let store = null
  let storeStatePropertyName = null

  function addStateFlag(name) {
    stateFlags = [...[name], ...R.without([name], stateFlags)]
    updateStore()
    return {
      clear: () => clearStateFlag(name),
      add: addStateFlag,
      reset,
    }
  }

  function clearStateFlag(name) {
    stateFlags = [...R.without([name], stateFlags)]
    updateStore()
  }

  function reset() {
    stateFlags = []
    updateStore()
  }

  function hasStateFlag(names = []) {
    if (typeof names === 'string') {
      names = [names]
    }
    return names.some((name) => stateFlags.includes(name))
  }

  function updateStore() {
    if (!!store && !!storeStatePropertyName) {
      store.state[storeStatePropertyName] = Date.now()
    }
  }

  /**
   * Optional bind a Vue vuex store to sync a timestamp (For reactivity)
   * @param {*} _store
   * @param {*} statePropertyName
   */
  function bindStore(_store, statePropertyName) {
    store = _store
    storeStatePropertyName = statePropertyName
    updateStore()
  }

  return {
    getStateFlags: () => stateFlags,
    addStateFlag, //Legacy/Deprecated
    add: addStateFlag,
    clear: clearStateFlag,
    reset,
    has: hasStateFlag,
    includes: hasStateFlag,
    bindStore,
  }
}
